import { Facility } from '@antification/core';
import { Camera } from './Camera';

/**
 * What a federal facility looks like.
 *
 * A slab, a windowless block, a stack of cooling fans on the roof, and a small
 * insignia. Nothing about it is in the register the rest of Parkington is drawn
 * in: no cream walls, no peaked roof, no lit sign inviting anybody in. §6 calls a
 * data centre's traffic profile *"minimal — commutes only"* and its emissions
 * *"hum, noise r=2, glows at night"*, and a building whose entire relationship
 * with the town is a hum should not look like a shop.
 *
 * The fans are the one thing that changes. §4's production rule is
 * `min(power, water, staffing) × capacity`, and the two terms this build can
 * measure are drawn: at full uptime every fan is lit, and at a fraction of it
 * that many fans are. A data centre the district could not fully supply is
 * visibly running slower — Law 2 applied to the only measurement a facility
 * currently makes, in the same spirit as an unlit house and a brown lawn.
 *
 * **Before any of that there is a building site**, for §7's `k` seasons: the slab,
 * a hoarding around it, and a stack of delivered goods that grows with the hoard.
 * No walls, no roof, no fans, no insignia — a mayor watching their meadow become
 * concrete finds out what is being built on it by waiting, exactly as they find
 * out everything else. What is drawn is what the ground shows: somebody's fence,
 * and lorries arriving at it.
 *
 * Deliberately absent: a noise radius, a night glow, a progress bar, a
 * "staffed" indicator. Each of those would draw a mechanic that does not exist —
 * the noise radius belongs to §12's federal overlay, which is Phase 4's and is
 * toggled rather than always on, and a progress bar would be the interface
 * telling a mayor how long the lorries have left when nothing else about the
 * estate has ever explained itself.
 */
export class FacilityRenderer {
  /**
   * @param uptime what fraction of its demand the estate got, in [0, 1]. 1 when
   * the district met it, which is also what a district with no shortage reports.
   * @param hoard goods delivered and not yet built with, for a site under
   * construction. Ignored once the facility has opened.
   * @param needs goods one season of its construction takes, so the stack is a
   * proportion rather than a count.
   */
  render(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    facility: Facility,
    uptime = 1,
    hoard = 0,
    needs = 0
  ): void {
    if (facility.openedTick === null) {
      this.renderSite(ctx, camera, facility, needs > 0 ? hoard / needs : 0);
      return;
    }
    const scale = camera.getScale();
    const cellW = camera.cellWidth * scale;
    const cellH = camera.cellHeight * scale;

    // Centre of the footprint, in the same terms `HouseRenderer` uses.
    const centerGridX = facility.x + (facility.cols - 1) / 2;
    const centerGridY = facility.y + (facility.rows - 1) / 2;
    const screen = camera.gridToScreen(centerGridX, centerGridY);
    const x = screen.x;
    const y = screen.y;

    const slabWidth = cellW * facility.cols * 0.98;
    const slabDepth = cellH * facility.rows * 0.98;
    const baseWidth = cellW * facility.cols * 0.62;
    const baseDepth = cellH * facility.rows * 0.62;
    const wallHeight = 26 * scale;

    ctx.save();

    // The slab: the whole footprint, and the reason the meadow under it is gone.
    ctx.fillStyle = '#8f9296';
    ctx.beginPath();
    ctx.moveTo(x, y - slabDepth / 2);
    ctx.lineTo(x + slabWidth / 2, y);
    ctx.lineTo(x, y + slabDepth / 2);
    ctx.lineTo(x - slabWidth / 2, y);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = 'rgba(40, 44, 48, 0.45)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Left wall
    ctx.fillStyle = '#6d747c';
    ctx.beginPath();
    ctx.moveTo(x - baseWidth / 2, y);
    ctx.lineTo(x, y + baseDepth / 2);
    ctx.lineTo(x, y + baseDepth / 2 - wallHeight);
    ctx.lineTo(x - baseWidth / 2, y - wallHeight);
    ctx.closePath();
    ctx.fill();

    // Right wall
    ctx.fillStyle = '#565c63';
    ctx.beginPath();
    ctx.moveTo(x, y + baseDepth / 2);
    ctx.lineTo(x + baseWidth / 2, y);
    ctx.lineTo(x + baseWidth / 2, y - wallHeight);
    ctx.lineTo(x, y + baseDepth / 2 - wallHeight);
    ctx.closePath();
    ctx.fill();

    // Flat roof
    ctx.fillStyle = '#7d848c';
    ctx.beginPath();
    ctx.moveTo(x - baseWidth / 2, y - wallHeight);
    ctx.lineTo(x, y - baseDepth / 2 - wallHeight);
    ctx.lineTo(x + baseWidth / 2, y - wallHeight);
    ctx.lineTo(x, y + baseDepth / 2 - wallHeight);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = 'rgba(30, 34, 38, 0.5)';
    ctx.stroke();

    // A single service door on the left wall. Eight people work here and this is
    // how they get in; there is no lobby and no sign, because nobody else comes.
    const doorW = 4 * scale;
    const doorH = 8 * scale;
    const isoSlope = baseDepth / baseWidth;
    const doorX = x - baseWidth / 5;
    const doorY = y + baseDepth / 4;
    ctx.fillStyle = '#3b4046';
    ctx.beginPath();
    ctx.moveTo(doorX, doorY);
    ctx.lineTo(doorX + doorW, doorY + doorW * isoSlope);
    ctx.lineTo(doorX + doorW, doorY + doorW * isoSlope - doorH);
    ctx.lineTo(doorX, doorY - doorH);
    ctx.closePath();
    ctx.fill();

    // The fans. `lit` of them are running; the rest are the curtailment.
    const FANS = 5;
    const lit = Math.round(Math.min(1, Math.max(0, uptime)) * FANS);
    const fanRadius = 2.4 * scale;
    const roofY = y - wallHeight;
    for (let i = 0; i < FANS; i++) {
      const t = (i - (FANS - 1) / 2) / FANS;
      const fx = x + t * baseWidth * 0.75;
      const fy = roofY + t * baseDepth * 0.75 * 0.5 - baseDepth * 0.06;
      ctx.fillStyle = i < lit ? '#9fe6c8' : '#454b52';
      ctx.beginPath();
      ctx.ellipse(fx, fy, fanRadius, fanRadius * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(24, 28, 32, 0.6)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    // The insignia: whose building this is, in the only mark the game has for a
    // polity. Small, on the roof, facing nobody.
    ctx.fillStyle = 'rgba(230, 236, 240, 0.75)';
    ctx.beginPath();
    ctx.arc(x, roofY - baseDepth * 0.28, 2 * scale, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  /**
   * A federal building site: the slab, a hoarding, and the goods on it.
   *
   * `filled` is the season's hoard as a fraction, so the stack of crates grows as
   * the lorries arrive and empties when a season of progress is banked. That is
   * the one honest progress indicator available — it is a *count of things on the
   * ground*, not a schedule, and it goes back down.
   */
  private renderSite(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    facility: Facility,
    filled: number
  ): void {
    const scale = camera.getScale();
    const cellW = camera.cellWidth * scale;
    const cellH = camera.cellHeight * scale;

    const centerGridX = facility.x + (facility.cols - 1) / 2;
    const centerGridY = facility.y + (facility.rows - 1) / 2;
    const screen = camera.gridToScreen(centerGridX, centerGridY);
    const x = screen.x;
    const y = screen.y;

    const slabWidth = cellW * facility.cols * 0.98;
    const slabDepth = cellH * facility.rows * 0.98;

    ctx.save();

    // The slab, in the same grey the finished thing stands on. It is the same
    // concrete: the ground goes on the day of the pour and the machine hall is
    // what takes two seasons.
    ctx.fillStyle = '#8f9296';
    ctx.beginPath();
    ctx.moveTo(x, y - slabDepth / 2);
    ctx.lineTo(x + slabWidth / 2, y);
    ctx.lineTo(x, y + slabDepth / 2);
    ctx.lineTo(x - slabWidth / 2, y);
    ctx.closePath();
    ctx.fill();

    // The hoarding: a low fence right around the footprint, in the yellow every
    // building site in the world is fenced in.
    ctx.strokeStyle = '#c8a53c';
    ctx.lineWidth = Math.max(1, 2 * scale);
    ctx.setLineDash([6 * scale, 4 * scale]);
    ctx.beginPath();
    ctx.moveTo(x, y - slabDepth / 2);
    ctx.lineTo(x + slabWidth / 2, y);
    ctx.lineTo(x, y + slabDepth / 2);
    ctx.lineTo(x - slabWidth / 2, y);
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // The goods, stacked where the lorries left them. Up to eight crates, which
    // is a quantity a person can count at a glance and is the only figure about
    // this site anything on screen gives.
    const crates = Math.round(Math.min(1, Math.max(0, filled)) * 8);
    const crateW = 5 * scale;
    const crateH = 4 * scale;
    for (let i = 0; i < crates; i++) {
      const row = Math.floor(i / 4);
      const col = i % 4;
      const cx = x + (col - 1.5) * crateW * 1.4;
      const cy = y + slabDepth * 0.16 - row * crateH * 1.2;
      ctx.fillStyle = row === 0 ? '#8a6f45' : '#9c7f50';
      ctx.fillRect(cx - crateW / 2, cy - crateH, crateW, crateH);
      ctx.strokeStyle = 'rgba(30, 26, 20, 0.5)';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(cx - crateW / 2, cy - crateH, crateW, crateH);
    }

    ctx.restore();
  }
}
